/**
 * TopLikedServicesFan — Top 3 servicios más likeados de una modelo, visible para fans.
 * Se muestra en TipPage justo debajo de los pills de categoría.
 * Solo aparece si la modelo tiene al menos 1 servicio con likes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Heart, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "../lib/api";

const RANK_STYLES = [
  { emoji: "🥇", border: "border-amber-400/50", bg: "bg-amber-400/10", text: "text-amber-300" },
  { emoji: "🥈", border: "border-zinc-400/40",  bg: "bg-zinc-400/5",   text: "text-zinc-300"  },
  { emoji: "🥉", border: "border-orange-700/40", bg: "bg-orange-700/5", text: "text-orange-300" },
];

export function TopLikedServicesFan({ modelId }) {
  const [top, setTop] = useState(null);

  useEffect(() => {
    if (!modelId) return;
    let alive = true;
    axios
      .get(`${API}/models/${modelId}/top-liked-services`)
      .then(r => { if (alive) setTop(r.data?.top || []); })
      .catch(() => alive && setTop([]));
    return () => { alive = false; };
  }, [modelId]);

  // No renderizar hasta cargar, ni si no hay servicios con likes
  if (!top || top.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="rounded-xl border border-neon-pink/20 bg-gradient-to-br from-neon-pink/5 to-purple-500/5 p-3 mb-3"
      data-testid="top-liked-services-fan"
    >
      <div className="flex items-center gap-1.5 mb-2">
        <Trophy className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
        <p className="text-xs font-heading font-bold text-zinc-300 uppercase tracking-wide">
          Los favoritos de sus fans
        </p>
      </div>

      <div className="space-y-1.5">
        {top.map((svc, idx) => {
          const style = RANK_STYLES[idx] || RANK_STYLES[0];
          const isFirst = idx === 0;
          return (
            <div
              key={svc.id}
              className={`flex items-center gap-2.5 px-3 ${isFirst ? "py-2.5" : "py-1.5"} rounded-lg border ${style.border} ${style.bg}`}
              data-testid={`fan-top-liked-rank-${idx + 1}`}
            >
              <span className={`${isFirst ? "text-xl" : "text-base"} shrink-0`} aria-hidden>
                {style.emoji}
              </span>
              <p
                className={`flex-1 min-w-0 font-heading ${isFirst ? "text-sm" : "text-xs"} ${style.text} font-bold truncate`}
                title={svc.name}
              >
                {svc.name}
              </p>
              <div className="flex items-center gap-1 shrink-0">
                <Heart className={`${isFirst ? "w-3.5 h-3.5" : "w-3 h-3"} text-neon-pink`} fill="currentColor" />
                <span className={`${isFirst ? "text-sm" : "text-xs"} font-heading font-bold text-white tabular-nums`}>
                  {svc.likes_count}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
